/**
 * Centralized Icon System
 *
 * This file exports all icons used in the Cadence app.
 *
 * Icon Sources:
 * - Lucide React: Standard icons (chevron, plus, edit, etc.)
 * - Custom: Specialized icons that don't exist in Lucide
 * - Brand: SchoolAI logos and brand assets
 *
 * Usage:
 * ```tsx
 * import { ChevronDown, Plus, Edit } from '@/components/icons'
 *
 * <ChevronDown className="w-4 h-4" />
 * <Plus size={20} />
 * ```
 */

// ============================================================================
// Lucide React Icons (Standard Icons)
// ============================================================================

export {
  // Navigation & Dropdowns
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp, // Issue #2950 - Collapse all
  ChevronsUpDown, // Issue #2950 - Expand all
  Home,
  Backpack,
  // Actions
  Plus,
  PlusCircle,
  X,
  Check,
  Edit,
  Edit2,
  Edit3,
  Pencil,
  Trash2 as Trash,
  Trash2,
  Copy,
  Save,
  Send,
  Minus,
  RefreshCw,
  SkipForward,
  IndentIncrease,
  IndentDecrease,
  // Menus & More
  MoreVertical,
  MoreHorizontal,
  Menu,
  Ban, // Issue #4661 - "None" filter option icon
  // Search & Filter
  Search,
  Filter,
  Sliders,
  SlidersHorizontal,
  Target,
  ArrowUpDown,
  // Status & Time
  Clock,
  Calendar,
  CalendarDays,
  Sun,
  Moon,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Info,
  Cloud,
  CloudOff,
  WifiOff,
  // Feedback Types
  Bug,
  HelpCircle,
  // View & Layout
  List,
  ListChecks,
  CheckSquare,
  LayoutGrid as Grid,
  LayoutDashboard,
  LayoutTemplate,
  Columns,
  Grip,
  GripVertical,
  PanelRight,
  PanelRightOpen,
  PanelLeftClose,
  PanelLeftOpen,
  Rows2,
  Rows3,
  Maximize2,
  Minimize2,
  // Rich Text Editor
  Bold,
  Italic,
  Heading2,
  Quote,
  ListOrdered,
  // Content
  Camera, // Issue #1997 - Screenshot capture
  Mic, // Issue #13063 - AI Notetaker capture
  MicOff, // Issue #13810 - the C3 "Not recorded" marker
  Paperclip, // Issue #2258, #2256 - Attachments
  User,
  UserPlus,
  Users,
  UsersRound,
  UserSearch,
  Mail,
  Link,
  Link2,
  Link2Off,
  File,
  FileText,
  FilePlus,
  FileType,
  FileCode,
  Code,
  Receipt,
  FileCheck2,
  FileDiff,
  Image,
  Upload,
  Download,
  Folder,
  Folders, // Issue #2864 - Plural icon for Projects navigation
  FolderTree,
  FolderOpen,
  ClipboardList,
  BookOpen,
  Library,
  Heart,
  // Arrows & Navigation
  ArrowLeft,
  ArrowRight,
  ArrowLeftRight,
  ArrowRightLeft,
  ArrowUp,
  ArrowDown,
  MoveRight,
  RotateCcw,
  CornerDownRight,
  CornerDownLeft,
  // Split a recording at a cut line (Issue #16997)
  Scissors,
  // Undo/Redo (Issue #2018)
  Undo2,
  Redo2,
  // Other
  Settings,
  Eye,
  EyeOff,
  ExternalLink,
  // Zoom (Issue #2002 - Screenshot preview)
  ZoomIn,
  ZoomOut,
  // Roles & Permissions
  Crown,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldPlus,
  ShieldX,
  // Lucide's outlined Lock — the barrel's `Lock` (in the Custom Icons section
  // below) is a filled lock used for the design-system lock-indicator pattern.
  // `LockOutline` preserves the outlined visual where it was previously
  // imported directly from lucide.
  Lock as LockOutline,
  Power,
  PowerOff,
  PenLine,
  NotebookPen,
  Binoculars,
  Package,
  Loader2,
  // Feedback
  ThumbsDown,
  ThumbsUp,
  Share,
  Share2,
  ClipboardCheck,
  // Activity & Comments
  Activity,
  HeartPulse,
  MessageCircle,
  MessageCirclePlus,
  MessageSquare,
  MessageSquarePlus,
  History,
  FileEdit,
  ArrowUpRight,
  Reply,
  // App Branding
  Radar,
  // Demo & Development
  Drama,
  Blocks,
  TestTube2,
  // Groups
  Group,
  Landmark,
  Component,
  // Item Types
  Mountain,
  Dices,
  // Features
  Coffee,
  Cake,
  Keyboard,
  KeyRound, // Issue #5540 - Rippling API key connect/rotate buttons
  Printer, // Issue #2969 - Print coaching prep view
  Presentation,
  Inbox,
  Compass,
  LogIn,
  LogOut,
  Play,
  Pause, // Issue #2173 - Training assignment pause/resume
  Monitor,
  // Notifications (Issue #1462, #2110, #2112)
  Bell,
  BellOff, // Issue #2110 - Muted subscription state
  AtSign, // Issue #2110 - Mentions only subscription state
  CheckCheck,
  FileSpreadsheet,
  Flame, // Issue #2112 - Urgency indicator
  // User States
  UserCheck,
  UserCircle, // Issue #6468 - Unified user settings hub Profile nav icon
  UserMinus,
  UserX,
  // Dashboard Icons
  BarChart3,
  Palette, // Issue #5213 - Design system metrics dashboard
  GanttChart, // Issue #3746 - Gantt chart roadmap view
  Calculator,
  DollarSign,
  Hash, // Issue #2500 - Number type measurable icon
  Percent, // Issue #2500 - Percentage type measurable icon
  CaseSensitive, // Issue #10273 - Text type measurable icon
  LineChart, // Issue #2151 - Chart block icon
  Rocket,
  // Table2 is exported under both names so consumers can import either
  // `Table` (existing alias) or `Table2` (lucide's canonical name).
  Table2 as Table, // Issue #2151 - Data table block icon
  Table2,
  TrendingUp,
  TrendingDown,
  Trophy,
  Zap,
  Wrench,
  Lightbulb,
  Handshake,
  HandHelping, // Issue #1162 - My Contributions
  Building2,
  Database,
  // Official/Badge indicators (Issue #2969)
  BadgeCheck,
  // Status Indicators
  Circle,
  Dot,
  Star,
  Award,
  Sparkles,
  Snowflake,
  // Agent/AI Icons (Issue #1763)
  Bot,
  BotMessageSquare,
  Cpu, // Issue #4643 - AI model configuration admin
  StopCircle,
  Box,
  // Company Info
  Gauge,
  Gem,
  Stone, // Issue #1127 - Rock default icon
  Archive,
  ArchiveRestore,
  FolderPlus,
  Bookmark,
  // Hierarchy & Inheritance
  GitBranch,
  GitMerge,
  GitPullRequest,
  GitPullRequestDraft,
  GitCompare,
  Equal,
  Network, // Issue #1666 - Distribution scope ALL_DESCENDANTS
  // Additional terminology icons
  Flag,
  Crosshair, // Issue #9727 - Weekly Focus section icon
  ArrowBigRightDash, // #14159 - spine Follow Along / Jump to Current
  Briefcase,
  Layers,
  Scale, // Issue #4611 - Calibration nav
  Square,
  SquareCheck, // Issue #3491 - Tasks icon
  // Integrations
  Cable,
  // Organization
  Globe,
  // Planning
  Map,
  MapPin, // Issue #2468 - Target parent destination indicator
  MapPinned, // Issue #2755 - first used by the revenue map; now the L10 spine
  // Training (Issue #2168)
  GraduationCap,
  // Issue #2015 - Control bar content toggle indicators
  ShieldQuestionMark,
  BadgeQuestionMark,
  // Meetings (Issue #2210)
  CalendarCheck,
  CalendarClock, // Issue #4977 - Scheduled Jobs admin nav
  CalendarX2,
  Megaphone, // Issue #10909 - Housekeeping section icon
  // Contractors (Issue #2646)
  IdCardLanyard,
  // Meetings/Calendar (Issue #8539)
  Video,
  // Linear integration tab
  Kanban,
  // Integrations admin tab
  Plug,
  // Tags (Issue #9847 - Arbitrary tags for work items)
  Tag,
  Tags,
} from 'lucide-react'

// ============================================================================
// Custom Icons (Specialized Designs)
// ============================================================================

export { Lock } from './custom/lock'
export { Unlock } from './custom/unlock'
export { GraphView } from './custom/graph-view'
export { Recenter } from './custom/recenter'
export { ExportPng } from './custom/export-png'

// Hierarchy Level Icons (Military rank-inspired)
export { HierarchyLevel1 } from './custom/hierarchy-level-1'
export { HierarchyLevel2 } from './custom/hierarchy-level-2'
export { HierarchyLevel3 } from './custom/hierarchy-level-3'
export { HierarchyLevel4 } from './custom/hierarchy-level-4'
export { HierarchyLevel5 } from './custom/hierarchy-level-5'
export { HierarchyLevel6 } from './custom/hierarchy-level-6'

// ============================================================================
// Brand Icons (SchoolAI Logos)
// ============================================================================

export { SchoolAILogoFull } from './brand/schoolai-logo-full'
export { SchoolAILogoSquare } from './brand/schoolai-logo-square'
export { SlackLogo } from './brand/slack-logo'
export {
  CadenceLogo,
  CadenceLogoSolid,
  CadenceLogoWhite,
} from './brand/cadence-logo'

// ============================================================================
// Types
// ============================================================================

// `LucideProps` is exported under both names: the original name
// (so the no-direct-lucide-react-import autofix produces a clean
// import for `import type { LucideProps } from 'lucide-react'`
// without a manual rename) AND the project alias `IconProps`
// (the preferred name in app code). Both resolve to the same type.
// See #6206 for context.
export type {
  LucideIcon,
  LucideProps,
  LucideProps as IconProps,
} from 'lucide-react'
